import { useEffect, useState } from 'react';

function ConfettiLayer() {
  const [pieces] = useState(() =>
    Array.from({ length: 80 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.2,
      duration: 2.5 + Math.random() * 2,
      // Tatweer-aligned palette: navy, green, gold, accents
      color: ['#19355C', '#2D9A5F', '#D4A843', '#1F4070', '#258751'][Math.floor(Math.random() * 5)],
      size: 6 + Math.random() * 8,
      rotate: Math.random() * 360,
    })),
  );

  return (
    <div className="fixed inset-0 z-30 pointer-events-none overflow-hidden">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute block animate-confetti"
          style={{
            left: `${p.left}%`,
            top: '-20px',
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: p.color,
            borderRadius: '2px',
            transform: `rotate(${p.rotate}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

export type RevealLeader = {
  ar: string;
  en: string;
  value_sar: number;
  count: number;
};

export function RevealOverlay({ leader }: { leader: RevealLeader | null }) {
  const [stage, setStage] = useState<1 | 2 | 3>(1);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(2), 1400);
    const t2 = setTimeout(() => setStage(3), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <>
      <ConfettiLayer />
      <div className="fixed inset-0 z-40 pointer-events-none flex items-start justify-center p-6 sm:p-10">
        <div className="rounded-3xl bg-white ring-1 ring-tatweer-navy/20 shadow-card-lg px-8 sm:px-12 py-8 sm:py-10 animate-reveal-in max-w-3xl">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-tatweer-green font-semibold mb-2">The reveal</div>

          <div className={`transition-all duration-700 ${stage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="text-3xl sm:text-5xl font-bold text-tatweer-navy mb-1">This entire dashboard</div>
            <div className="text-lg sm:text-2xl font-semibold text-tatweer-body-soft">— its data, its layout, its live responses —</div>
          </div>

          <div className={`transition-all duration-700 mt-4 ${stage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="text-2xl sm:text-4xl font-bold text-tatweer-green">was built and is running autonomously.</div>
          </div>

          {leader && (
            <div className={`transition-all duration-700 mt-6 pt-6 border-t border-tatweer-line ${stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-baseline justify-between gap-6 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-tatweer-muted">الأعلى تصدّراً للمناقصات</div>
                  <div className="text-xl sm:text-2xl font-bold text-tatweer-navy mt-1 truncate">{leader.ar}</div>
                  <div className="text-sm text-tatweer-body-soft tabular-nums mt-0.5">
                    {leader.count.toLocaleString()} مناقصة · Top by tender value
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-5xl sm:text-7xl font-extrabold text-tatweer-green tabular-nums leading-none">
                    {(leader.value_sar / 1e9).toFixed(2)}
                  </div>
                  <div className="text-sm text-tatweer-green-600 mt-1">مليار ريال · Billion SAR</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
